import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import { AdventureDetailPayload, AdventureCommentPayload, FlightPayload, MediaPayload } from './types'
import { Button } from '../components/button'
import { PencilIcon, TrashIcon, PlusIcon, PaperAirplaneIcon, ArrowLeftIcon, PhotoIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../components/dialog'
import { useAuthStatus } from './user_dropdown'
import { PilotAvatar } from './pilot_avatar'
import { RichTextEditor } from './flight_post'
import { FlightMapView } from './flview'
import { MapContainer, Pane, Polyline, TileLayer, useMap } from 'react-leaflet'
import { FlightReplay3D } from './flview/FlightReplay3D'
import { isReplay3DRequested, replay3DSearchParams } from './flview/FlightReplay3D.location'
import { getMapFitPositions } from './flview/FlightMapView.bounds'
import { buildAdventureMapLines, adventurePilotColors } from './adventure_map_lines'

function InlineTitle({ adventureId, initial, editable }: { adventureId: number; initial: string; editable: boolean }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(initial)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { setValue(initial) }, [initial])
  useEffect(() => { if (editing) inputRef.current?.select() }, [editing])

  const save = async () => {
    setEditing(false)
    await fetch(`/v0/adventure/${adventureId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title: value.trim() }),
    })
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') save()
    if (e.key === 'Escape') { setValue(initial); setEditing(false) }
  }

  if (editing && editable) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={onKeyDown}
        className="text-2xl font-bold text-[#2D3142] dark:text-white bg-transparent border-b-2 border-[#EF8354] outline-none w-full"
      />
    )
  }

  if (!editable) {
    return <span className="text-2xl font-bold text-[#2D3142] dark:text-white">{value}</span>
  }

  return (
    <button onClick={() => setEditing(true)} className="group flex items-center gap-2 text-left">
      <span className="text-2xl font-bold text-[#2D3142] dark:text-white">{value}</span>
      <PencilIcon className="h-4 w-4 text-[#4F5D75] dark:text-[#BFC0C0] opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  )
}

function AdventureMap({ flights }: { flights: FlightPayload[] }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const replayOpen = isReplay3DRequested(searchParams)
  const setReplayOpen = useCallback((open: boolean) => {
    setSearchParams(current => replay3DSearchParams(current, open))
  }, [setSearchParams])
  const shareUrl = `${location.pathname}?${replay3DSearchParams(searchParams, true)}${location.hash}`
  const replayFlights = useMemo(() => {
    const colors = adventurePilotColors(flights)
    return flights.filter(flight => flight.json_track?.length).map(flight => ({
      id: String(flight.id), pilotId: flight.pilot_id,
      label: flight.pilot_name || flight.pilot_nick || `Pilot #${flight.pilot_id}`,
      color: colors.get(flight.pilot_id)!,
      track: flight.json_track.map(point => ({
        ...point,
        timestamp: typeof point.timestamp === 'string' ? point.timestamp : point.timestamp.toISOString(),
      })),
    }))
  }, [flights])
  const { flightLines, upTrackLines } = useMemo(() => buildAdventureMapLines(flights), [flights])

  const fitPositions = useMemo(
    () =>
      getMapFitPositions(
        flights.flatMap((flight) => flight.json_track || []),
        flights.flatMap((flight) => flight.json_up_track || []),
      ),
    [flights],
  )

  const initialCenter: [number, number] = useMemo(() => {
    const firstPoint =
      flights.find((flight) => flight.json_track?.length)?.json_track[0] ||
      flights.find((flight) => flight.json_up_track?.length)?.json_up_track?.[0]

    return firstPoint ? [firstPoint.coordinates.lat, firstPoint.coordinates.lon] : [46.5, 6.6]
  }, [flights])

  if (flights.length === 0) {
    return (
      <div className="h-80 bg-gray-100 dark:bg-[#4F5D75]/30 rounded-lg flex items-center justify-center">
        <p className="text-[#4F5D75] dark:text-[#BFC0C0]">Add flights to see their tracks on the map</p>
      </div>
    )
  }

  return (
    <>
    <section className="flview-flight-map-card">
      {replayFlights.length > 0 && (
        <div className="flex justify-end p-3">
          <Button color="airtime" onClick={() => setReplayOpen(true)}>3D replay</Button>
        </div>
      )}
      <div className="flview-map-layout">
        <div className="flview-map-shell !min-h-0">
          <MapContainer className="flview-map-canvas !h-80" center={initialCenter} zoom={13} scrollWheelZoom>
            <TileLayer
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              attribution='Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>'
            />

            <AdventureFitToBounds positions={fitPositions} />

            <Pane name="adventure-up-track" style={{ zIndex: 410 }}>
              {upTrackLines.map((line) => (
                <Polyline
                  key={line.id}
                  positions={line.positions}
                  pathOptions={{ color: line.color, weight: 4, opacity: 0.72 }}
                />
              ))}
            </Pane>

            <Pane name="adventure-flight-track" style={{ zIndex: 420 }}>
              {flightLines.map((line) => (
                <Polyline
                  key={line.id}
                  positions={line.positions}
                  pathOptions={{ color: line.color, weight: 4, opacity: 0.92 }}
                />
              ))}
            </Pane>
          </MapContainer>
        </div>
      </div>
    </section>
    {replayOpen && replayFlights.length > 0 && (
      <FlightReplay3D flights={replayFlights} onClose={() => setReplayOpen(false)} shareUrl={shareUrl} />
    )}
    </>
  )
}

function AdventureFitToBounds({ positions }: { positions: [number, number][] }) {
  const map = useMap()

  useEffect(() => {
    if (!positions.length) {
      return
    }

    let raf: number
    const fit = () => {
      raf = requestAnimationFrame(() => {
        map.invalidateSize()
        map.fitBounds(positions, {
          padding: [40, 40],
          maxZoom: 15,
          animate: false,
        })
      })
    }

    map.whenReady(fit)

    return () => cancelAnimationFrame(raf)
  }, [map, positions])

  return null
}

function AdventurePhotoSection({ adventureId, photos, isOwner, onPhotosChange }: {
  adventureId: number
  photos: MediaPayload[]
  isOwner: boolean
  onPhotosChange: () => void
}) {
  const [uploading, setUploading] = useState(false)
  const [viewerIdx, setViewerIdx] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await fetch(`/v0/adventure/${adventureId}/photos`, { method: 'POST', body: form })
      if (res.ok) onPhotosChange()
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white flex items-center gap-2">
          <PhotoIcon className="h-5 w-5 text-[#EF8354]" /> Photos
        </h2>
        {isOwner && (
          <label className="cursor-pointer">
            <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
            <span className="inline-flex items-center gap-1 rounded-md bg-[#EF8354] px-2.5 py-1 text-xs font-medium text-white hover:bg-[#EF8354]/80 transition-colors">
              <PlusIcon className="h-4 w-4" /> {uploading ? 'Uploading…' : 'Add Photo'}
            </span>
          </label>
        )}
      </div>
      {photos.length === 0 ? (
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">No photos yet.</p>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, idx) => (
            <button key={p.id} onClick={() => setViewerIdx(idx)} className="rounded-lg overflow-hidden aspect-square">
              <img src={`/v0/media/${p.id}`} alt={p.filename} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {viewerIdx !== null && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center" onClick={() => setViewerIdx(null)}>
          <button className="absolute top-4 right-4 text-white" onClick={() => setViewerIdx(null)}>
            <XMarkIcon className="h-8 w-8" />
          </button>
          {viewerIdx > 0 && (
            <button className="absolute left-4 text-white" onClick={e => { e.stopPropagation(); setViewerIdx(viewerIdx - 1) }}>
              <ChevronLeftIcon className="h-10 w-10" />
            </button>
          )}
          {viewerIdx < photos.length - 1 && (
            <button className="absolute right-14 text-white" onClick={e => { e.stopPropagation(); setViewerIdx(viewerIdx + 1) }}>
              <ChevronRightIcon className="h-10 w-10" />
            </button>
          )}
          <img
            src={`/v0/media/${photos[viewerIdx].id}`}
            alt={photos[viewerIdx].filename}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  )
}

function CommentSection({ adventureId, comments, onCommentAdded }: {
  adventureId: number
  comments: AdventureCommentPayload[]
  onCommentAdded: () => void
}) {
  const { userData } = useAuthStatus()
  const [body, setBody] = useState('')
  const [posting, setPosting] = useState(false)

  const handlePost = async () => {
    if (!body.trim()) return
    setPosting(true)
    try {
      await fetch(`/v0/adventure/${adventureId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: body.trim() }),
      })
      setBody('')
      onCommentAdded()
    } finally {
      setPosting(false)
    }
  }

  const handleDelete = async (commentId: number) => {
    await fetch(`/v0/adventure/${adventureId}/comments/${commentId}`, { method: 'DELETE' })
    onCommentAdded()
  }

  return (
    <div>
      <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white mb-3">Comments</h2>
      <div className="space-y-3">
        {comments.map(c => (
          <div key={c.id} className="flex gap-3 p-3 rounded-lg bg-[#F7F7F8] dark:bg-[#4F5D75]/30">
            <PilotAvatar pilot={{ nick: c.pilot_nick, name: c.pilot_name, avatar: c.pilot_avatar } as any} className="h-8 w-8" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Link to={`/pilot/${c.pilot_nick}`} className="text-sm font-medium text-[#2D3142] dark:text-white hover:text-[#EF8354]">
                  {c.pilot_name}
                </Link>
                <span className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                  {new Date(c.created_at).toLocaleDateString()}
                </span>
                {userData.authenticated && userData.pilot?.id === c.pilot_id && (
                  <button onClick={() => handleDelete(c.id)} className="ml-auto text-[#4F5D75] hover:text-red-500">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] mt-1">{c.body}</p>
            </div>
          </div>
        ))}
      </div>

      {userData.authenticated && (
        <div className="mt-4 flex gap-2">
          <input
            type="text"
            placeholder="Write a comment…"
            value={body}
            onChange={e => setBody(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handlePost() }}
            className="flex-1 rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
          />
          <Button color="airtime" onClick={handlePost} disabled={!body.trim() || posting}>
            <PaperAirplaneIcon className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  )
}

function FlightListSection({ adventureId, flights, isOwner, onFlightsChange }: {
  adventureId: number
  flights: FlightPayload[]
  isOwner: boolean
  onFlightsChange: () => void
}) {
  const navigate = useNavigate()
  const colors = useMemo(() => adventurePilotColors(flights), [flights])
  const [addOpen, setAddOpen] = useState(false)
  const [flightIdInput, setFlightIdInput] = useState('')
  const [adding, setAdding] = useState(false)

  const handleAdd = async () => {
    const fid = parseInt(flightIdInput)
    if (isNaN(fid)) return
    setAdding(true)
    try {
      await fetch(`/v0/adventure/${adventureId}/flights/${fid}`, { method: 'PUT' })
      onFlightsChange()
      setAddOpen(false)
      setFlightIdInput('')
    } finally {
      setAdding(false)
    }
  }

  const handleRemove = async (flightId: number) => {
    await fetch(`/v0/adventure/${adventureId}/flights/${flightId}`, { method: 'DELETE' })
    onFlightsChange()
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white">Flights</h2>
        {isOwner && (
          <Button color="airtime" onClick={() => setAddOpen(true)}>
            <PlusIcon className="h-4 w-4" /> Add Flight
          </Button>
        )}
      </div>

      {flights.length === 0 ? (
        <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">No flights in this adventure yet.</p>
      ) : (
        <div className="space-y-4">
          {flights.map(f => (
            <div
              key={f.id}
              className="rounded-lg bg-[#F7F7F8] dark:bg-[#4F5D75]/30 overflow-hidden"
            >
              {/* Header row */}
              <div className="flex items-center justify-between p-3 hover:bg-[#BFC0C0]/30 dark:hover:bg-[#4F5D75]/50 transition-colors">
                <button
                  className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  onClick={() => navigate(`/flight/${f.id}`)}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: colors.get(f.pilot_id) }}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2D3142] dark:text-white truncate">
                      {f.title || f.takeoff_name || `Flight #${f.id}`}
                    </p>
                    <p className="text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                      {f.pilot_nick && <span>{f.pilot_nick} · </span>}
                      {new Date(f.start_time).toLocaleDateString()}
                      {f.statistics?.duration && ` · ${f.statistics.duration}`}
                    </p>
                  </div>
                </button>
                {isOwner && (
                  <button
                    onClick={() => handleRemove(f.id)}
                    className="ml-2 text-[#4F5D75] hover:text-red-500 flex-shrink-0"
                    title="Remove from adventure"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Per-flight map, chart, and stats */}
              {f.json_track && f.json_track.length > 0 && (
                <div>
                  <FlightMapView flight={f} showHeader={false} />
                  {f.statistics && (
                    <div className="px-3 pb-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#4F5D75] dark:text-[#BFC0C0]">
                      <span>⏱ {f.statistics.duration}</span>
                      <span>⬆ {f.statistics.max_gain} m gain</span>
                      <span>🏔 {f.statistics.top_altitude} m top</span>
                      <span>⌀ {f.statistics.average_speed_kmh?.toFixed(1)} km/h</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={addOpen} onClose={() => setAddOpen(false)}>
        <DialogTitle>Add Flight to Adventure</DialogTitle>
        <DialogDescription>Enter the flight ID to add.</DialogDescription>
        <DialogBody>
          <input
            type="number"
            placeholder="Flight ID"
            value={flightIdInput}
            onChange={e => setFlightIdInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleAdd() }}
            className="w-full rounded-md border border-[#BFC0C0]/50 dark:border-[#4F5D75]/50 bg-transparent px-3 py-2 text-sm text-[#2D3142] dark:text-white outline-none focus:border-[#EF8354] transition-colors"
          />
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setAddOpen(false)} disabled={adding}>Cancel</Button>
          <Button color="airtime" onClick={handleAdd} disabled={!flightIdInput || adding}>
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export function AdventureDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { userData } = useAuthStatus()
  const [detail, setDetail] = useState<AdventureDetailPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const fetchDetail = useCallback(() => {
    if (!id) return
    fetch(`/v0/adventure/${id}`)
      .then(r => { if (!r.ok) throw new Error('not found'); return r.json() })
      .then(async (data) => {
        // Enrich each flight with track data from the tracks endpoint
        if (data.flights && data.flights.length > 0) {
          const trackResults = await Promise.all(
            data.flights.map((f: FlightPayload) =>
              fetch(`/v0/flight/${f.id}/tracks`)
                .then(r => r.ok ? r.json() : { json_track: [], json_up_track: [] })
                .catch(() => ({ json_track: [], json_up_track: [] }))
            )
          )
          data.flights = data.flights.map((f: FlightPayload, i: number) => ({
            ...f,
            json_track: trackResults[i].json_track ?? [],
            json_up_track: trackResults[i].json_up_track ?? [],
          }))
        }
        setDetail(data)
      })
      .catch(() => navigate('/adventures'))
      .finally(() => setLoading(false))
  }, [id, navigate])

  useEffect(() => { fetchDetail() }, [fetchDetail])

  if (loading || !detail) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 animate-pulse space-y-4">
        <div className="h-8 w-64 bg-[#BFC0C0]/30 rounded" />
        <div className="h-80 bg-[#BFC0C0]/30 rounded-lg" />
      </div>
    )
  }

  const isOwner = userData.authenticated && userData.pilot?.id === detail.adventure.pilot_id

  const handleDelete = async () => {
    await fetch(`/v0/adventure/${id}`, { method: 'DELETE' })
    navigate('/adventures')
  }

  const handleDescriptionSave = async (html: string | null) => {
    await fetch(`/v0/adventure/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: html }),
    })
    fetchDetail()
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/adventures')} className="text-[#4F5D75] hover:text-[#EF8354]">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <InlineTitle adventureId={detail.adventure.id} initial={detail.adventure.title} editable={isOwner} />
        </div>
        {isOwner && (
          <Button color="red" onClick={() => setConfirmDelete(true)}>
            <TrashIcon className="h-4 w-4" />
          </Button>
        )}
      </div>

      <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0]">
        Created {new Date(detail.adventure.created_at).toLocaleDateString()}
      </p>

      {/* Map */}
      <AdventureMap flights={detail.flights} />

      {/* Description */}
      <div>
        <h2 className="text-lg font-semibold text-[#2D3142] dark:text-white mb-2">Description</h2>
        {isOwner ? (
          <RichTextEditor
            initial={detail.adventure.description || ''}
            placeholder="Write a description for this adventure…"
            onSave={handleDescriptionSave}
          />
        ) : detail.adventure.description ? (
          <div
            className="prose dark:prose-invert max-w-none text-sm text-[#4F5D75] dark:text-[#BFC0C0]"
            dangerouslySetInnerHTML={{ __html: detail.adventure.description }}
          />
        ) : (
          <p className="text-sm text-[#4F5D75] dark:text-[#BFC0C0] italic">No description yet.</p>
        )}
      </div>

      {/* Flights */}
      <FlightListSection
        adventureId={detail.adventure.id}
        flights={detail.flights}
        isOwner={isOwner}
        onFlightsChange={fetchDetail}
      />

      {/* Photos */}
      <AdventurePhotoSection
        adventureId={detail.adventure.id}
        photos={detail.photos}
        isOwner={isOwner}
        onPhotosChange={fetchDetail}
      />

      {/* Comments */}
      <CommentSection
        adventureId={detail.adventure.id}
        comments={detail.comments}
        onCommentAdded={fetchDetail}
      />

      {/* Delete confirmation */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Delete Adventure</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete this adventure? Flights will not be deleted but will be detached.
        </DialogDescription>
        <DialogActions>
          <Button plain onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="red" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
